import Handlebars from "handlebars";
import type {
  DataKeyResolution,
  DynamicContentDataKey,
} from "./dynamic-node-context";

/**
 * Shared resolution logic for the dynamic data-key / handlebars nodes. The backend
 * (`GET /document/:id/dynamic-content`) now performs the two-tier data-key
 * resolution + freezing and returns a resolved `dataKeys` map plus an always-live
 * non-answer `context` map. The frontend is a dumb consumer: data keys are a
 * lookup into `dataKeys`, context variables a lookup into `context`, and handlebars
 * fragments evaluate against both (dataKeys taking precedence).
 *
 * Whether a version is frozen is decided server-side (via `readyForApproval`); the
 * editor's `frozen` flag now drives STYLING only and never resolution.
 */

/**
 * Register the comparison / logic helpers used inside handlebars fragments. These
 * mirror `src/utils/documentGeneration.ts` so the editor evaluates templates the
 * same way the backend snapshot does. Registration is idempotent (overwrites),
 * and self-contained here so the resolver doesn't depend on documentGeneration's
 * heavy import graph being loaded first.
 */
const reduceOp = (
  args: IArguments,
  reducer: (a: unknown, b: unknown) => boolean
): boolean => {
  const list = Array.from(args);
  list.pop(); // drop the trailing Handlebars `options`
  const first = list.shift();
  return list.reduce(reducer as never, first as never) as unknown as boolean;
};

Handlebars.registerHelper("ifEquals", function (this: unknown, arg1, arg2, options) {
  // eslint-disable-next-line eqeqeq
  return arg1 == arg2 ? options.fn(this) : options.inverse(this);
});

Handlebars.registerHelper({
  eq: function () {
    return reduceOp(arguments, (a, b) => a === b);
  },
  ne: function () {
    return reduceOp(arguments, (a, b) => a !== b);
  },
  lt: function () {
    return reduceOp(arguments, (a, b) => (a as number) < (b as number));
  },
  gt: function () {
    return reduceOp(arguments, (a, b) => (a as number) > (b as number));
  },
  lte: function () {
    return reduceOp(arguments, (a, b) => (a as number) <= (b as number));
  },
  gte: function () {
    return reduceOp(arguments, (a, b) => (a as number) >= (b as number));
  },
  and: function () {
    return reduceOp(arguments, (a, b) => Boolean(a && b));
  },
  or: function () {
    return reduceOp(arguments, (a, b) => Boolean(a || b));
  },
  not: function (value: unknown) {
    return !value;
  },
});

const NULL_RESOLUTION: DataKeyResolution = {
  value: null,
  sourceDocumentTitle: null,
  sourceDocumentId: null,
  sourceDocumentDeviceId: null,
  isUnresolved: true,
};

export interface ResolverParams {
  /**
   * Backend-resolved data keys: the two answer tiers + freezing already applied,
   * keyed by element with value + provenance. A missing key resolves to null.
   */
  dataKeys: Record<string, DynamicContentDataKey>;
  /**
   * Non-answer context resolved by the backend: device + organization
   * characteristics (`device-is-mdr`, `organization-has-fda-devices`) and document
   * metadata (`documentIdentifier`, `company-name`, `revision`). Always-live.
   */
  context: Record<string, unknown>;
}

export interface DynamicNodeResolver {
  /** Resolve a data key against the backend `dataKeys` map ONLY (no context). */
  resolveDataKey: (dataKey: string) => DataKeyResolution;
  /** Resolve a context variable against the backend `context` map ONLY. */
  resolveContext: (contextKey: string) => DataKeyResolution;
  evaluateHandlebars: (template: string) => string;
}

/** Map a backend data-key entry to the node resolution shape consumed by the views. */
const toResolution = (entry: DynamicContentDataKey): DataKeyResolution => ({
  // Values arrive pre-serialized as markdown (file answers as `![](…)`); the chip
  // renders every resolved value through the editor schema, so no rich-content flag
  // is needed here.
  value: entry.value,
  sourceDocumentTitle: entry.provenance?.sourceDocumentTitle ?? null,
  sourceDocumentId: entry.provenance?.sourceDocumentId ?? null,
  sourceDocumentDeviceId: entry.provenance?.sourceDeviceId ?? null,
  isUnresolved: entry.isUnresolved,
});

/** Resolve a scalar context value to a node resolution, or null when absent. */
const resolveContextValue = (
  contextKey: string,
  context: Record<string, unknown>
): DataKeyResolution => {
  if (contextKey in context) {
    const contextValue = context[contextKey];
    // Only scalar values render as a node; objects/arrays aren't displayable.
    if (
      contextValue !== null &&
      contextValue !== undefined &&
      typeof contextValue !== "object"
    ) {
      return {
        value: String(contextValue),
        sourceDocumentTitle: null,
        sourceDocumentId: null,
        sourceDocumentDeviceId: null,
        isUnresolved: false,
      };
    }
  }
  return NULL_RESOLUTION;
};

/**
 * Builds the resolver pair consumed by the dynamic node views. Pure given its
 * params — memoize on the inputs in the provider.
 */
export const buildDynamicNodeResolver = (
  params: ResolverParams
): DynamicNodeResolver => {
  const { dataKeys, context } = params;

  // Data keys (answers) resolve via the backend map only — never context.
  const resolveDataKey = (dataKey: string): DataKeyResolution => {
    const entry = dataKeys[dataKey];
    return entry ? toResolution(entry) : NULL_RESOLUTION;
  };

  // Context keys (device/org characteristics, document metadata) resolve via the
  // backend context map only — a separate source from the data keys.
  const resolveContext = (contextKey: string): DataKeyResolution =>
    resolveContextValue(contextKey, context);

  /**
   * Resolve a key to a raw value for handlebars evaluation: data keys first, then
   * context. Conditionals/interpolation can reference either source, so this stays
   * combined even though the data-key and context NODES resolve separately.
   * Preserves dataKeys-first precedence so conditionals evaluate identically.
   */
  const resolveRaw = (key: string): unknown => {
    const entry = dataKeys[key];
    if (entry && entry.value != null) {
      return entry.value;
    }
    if (key in context) {
      return context[key];
    }
    return null;
  };

  const evaluateHandlebars = (template: string): string => {
    if (!template) {
      return "";
    }
    try {
      const renderContext: Record<string, unknown> = {};
      for (const key of collectReferencedKeys(template)) {
        const value = resolveRaw(key);
        if (value != null) {
          renderContext[key] = value;
        }
      }
      const compiled = Handlebars.compile(template, {
        noEscape: true,
        strict: false,
      });
      return compiled(renderContext);
    } catch {
      // A malformed fragment falls back to its raw template rather than throwing.
      return template;
    }
  };

  return { resolveDataKey, resolveContext, evaluateHandlebars };
};

/**
 * Collects the data-key identifiers referenced inside a handlebars template via
 * its AST (both branches of conditionals, helper params, etc.). Over-collecting
 * helper names (`if`, `eq`) is harmless — they simply resolve to null and helpers
 * take precedence over context. Returns [] for an unparseable fragment.
 */
export const collectReferencedKeys = (template: string): string[] => {
  const keys = new Set<string>();
  let ast: hbs.AST.Program;
  try {
    ast = Handlebars.parse(template);
  } catch {
    return [];
  }

  const visit = (node: unknown): void => {
    if (!node || typeof node !== "object") {
      return;
    }
    const typed = node as { type?: string; [key: string]: unknown };

    if (typed.type === "PathExpression") {
      const parts = (typed.parts as string[]) ?? [];
      if (!typed.data && parts.length === 1) {
        keys.add(parts[0]);
      }
    }

    for (const value of Object.values(typed)) {
      if (Array.isArray(value)) {
        value.forEach(visit);
      } else if (value && typeof value === "object") {
        visit(value);
      }
    }
  };

  visit(ast);
  return [...keys];
};
